// src/store/cartStore.ts
// ─────────────────────────────────────────────────────────────────────────────
// NIIZ ERP · Store del Carrito (POS / Restaurante / Marketplace)
// ─────────────────────────────────────────────────────────────────────────────
// Actualización v110:
//   · Cero `any` — tipado estricto con Product/ItemModifier de db.ts
//   · Conexión con motor financiero (Centavos, divRound)
//   · Conversión bidireccional caja/unidad (box_price, units_per_box)
//   · Soporte multi-sede (branch_id) y clasificación flexible (tags)
//   · 100% aditivo: toda la lógica de negocio existente se preserva
// ─────────────────────────────────────────────────────────────────────────────

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Product, ItemModifier } from '@/db/db';
import { C, divRound, type Centavos } from '@/core/financial-engine/money';

// ─────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────

/** Modo de venta: por caja completa o por unidad suelta. */
export type SaleMode = 'BOX' | 'UNIT';

export interface CartItem {
  id: string;
  cartItemId: string; // ID único para esta entrada en el carrito
  name: string;
  price: number;
  final_price: number;
  quantity: number;
  subtotal: number;
  unit?: string;
  image_url?: string | undefined;
  supplier_id?: string | undefined;
  supplier_name?: string;
  modifiers?: ItemModifier[] | undefined;
  ticket_destination?: 'KITCHEN' | 'BAR' | 'NONE';
  type: 'FISICO' | 'SERVICIO' | 'PLATO' | 'ESPACIO' | 'NO_COTIZABLE';

  // ── CONVERSIÓN DE UNIDAD (venta por unidad secundaria) ──
  // `exactOptionalPropertyTypes`: se declara `| undefined` explícito porque los
  // campos se asignan directamente desde `Product` (que también los tipa así).
  base_unit?: string | undefined;          // unidad base del producto (ej. KG, CAJA)
  sale_unit?: string | undefined;          // unidad en la que se vende (ej. GR, UNIDAD)
  conversion_to?: string | undefined;      // unidad secundaria a la que convierte
  conversion_factor?: number | undefined;  // cuántas unidades de sale_unit hay por 1 base_unit

  // ── 🆕 CONVERSIÓN CAJA/UNIDAD v110 ──
  /** Precio por caja completa (si el producto se vende por cajas). */
  box_price?: number | undefined;
  /** Cuántas unidades hay en una caja (ej: 12 unidades por caja). */
  units_per_box?: number | undefined;
  /** Modo de venta actual: BOX (caja completa) o UNIT (unidad suelta). */
  sale_mode?: SaleMode;

  // ── 🆕 MULTI-SEDE Y CLASIFICACIÓN FLEXIBLE v110 ──
  /** ID de la sucursal donde está el producto (multi-sede). ⚠️ migración v111. */
  branch_id?: string | undefined;
  /** Etiquetas dinámicas para clasificación flexible (columna REAL de Product). */
  tags?: string[] | undefined;

  // ── RESTAURANTE: modo de pago por comensal ──
  diner_id?: string | null | undefined;         // comensal al que se asigna el producto
  is_shared?: boolean | undefined;              // producto compartido entre comensales
  shared_diner_ids?: string[] | null | undefined; // entre quiénes se divide (por defecto: todos)
  sale_item_id?: string | undefined;            // id del registro en sale_items (si ya se guardó)
  saved_in_db?: boolean | undefined;

  // ── CANCELACIÓN DEL PRODUCTO (el registro no se borra) ──
  status?: 'ACTIVE' | 'CANCELLED' | undefined;
  cancelled_at?: string | null | undefined;
  cancelled_by?: string | null | undefined;
  cancel_reason_label?: string | null | undefined;
  cancel_notes?: string | null | undefined;
  cancel_was_prepared?: boolean | undefined;

  /** Nota de cocina por producto (ej: "sin cebolla", "bien cocido"). */
  item_note?: string | null | undefined;
}

interface CartStore {
  items: CartItem[];
  isOpen: boolean;
  currentCustomer: { id: string; name: string } | null;
  customer: { id: string; name: string } | null; // Alias para compatibilidad con POSPage

  // Acciones
  setIsOpen: (open: boolean) => void;
  addItem: (product: Product, modifiers?: ItemModifier[], options?: { forceNewLine?: boolean }) => void;
  removeItem: (cartItemId: string) => void;
  setItemSaleUnit: (cartItemId: string, saleUnit: string) => void;
  /** 🆕 Cambia el modo de venta: BOX (caja completa) o UNIT (unidad suelta). */
  setSaleMode: (cartItemId: string, mode: SaleMode) => void;
  updateQuantity: (cartItemId: string, qty: number) => void;
  increaseQuantity: (cartItemId: string) => void;
  decreaseQuantity: (cartItemId: string) => void;
  clearCart: () => void;
  setCustomer: (customer: { id: string; name: string } | null) => void;
  setItems: (items: CartItem[]) => void;
  /** Actualiza el precio unitario del ítem (ej. servicios de precio abierto) */
  updateItemPrice: (cartItemId: string, newPrice: number) => void;
  /** Actualiza campos concretos de un ítem (asignación a comensal, compartido, etc.) */
  updateItemFields: (cartItemId: string, patch: Partial<CartItem>) => void;

  // Helpers
  getSummary: () => {
    subtotal: number;
    total: number;
    taxAmount: number;
    discount: number;
    tipAmount: number;
  };
  getTotal: () => number;

  // ── 🆕 HELPERS FINANCIEROS (Centavos) ──
  /** Subtotal del carrito en Centavos (para motor financiero). */
  getSubtotalInCents: () => Centavos;
  /** Total del carrito en Centavos (para motor financiero). */
  getTotalInCents: () => Centavos;
  /** Precio de un ítem específico en Centavos. */
  getItemPriceInCents: (cartItemId: string) => Centavos;
  /** Subtotal de un ítem específico en Centavos. */
  getItemSubtotalInCents: (cartItemId: string) => Centavos;
}

// ─────────────────────────────────────────────────────────────
// HELPERS DE CONVERSIÓN FINANCIERA
// ─────────────────────────────────────────────────────────────

/** Convierte un precio en pesos a Centavos. */
const toCentavos = (amount: number): Centavos => C(BigInt(Math.round(amount * 100)));

/**
 * Sede del producto, si la fila legacy la trae.
 * ⚠️ `Product` (db.ts) NO declara `branch_id`: se lee con guarda runtime.
 */
const readLegacyBranchId = (product: Partial<Product>): string | undefined => {
  const raw: unknown = Reflect.get(product, 'branch_id');
  return typeof raw === 'string' && raw.trim() !== '' ? raw : undefined;
};

/**
 * Calcula el precio unitario según el modo de venta (BOX o UNIT).
 * Si el producto tiene box_price y units_per_box:
 *   - BOX: precio = box_price
 *   - UNIT: precio = box_price / units_per_box (con divRound para precisión)
 */
const calculateUnitPrice = (
  basePrice: number,
  boxPrice: number | undefined,
  unitsPerBox: number | undefined,
  saleMode: SaleMode,
): number => {
  // Si no hay conversión caja/unidad, usar precio base
  if (!boxPrice || !unitsPerBox || unitsPerBox === 0) {
    return basePrice;
  }

  if (saleMode === 'BOX') {
    return boxPrice;
  }

  // UNIT: precio por unidad = box_price / units_per_box
  // Usar divRound para precisión bancaria (HALF_EVEN)
  const boxPriceCents = toCentavos(boxPrice);
  const unitPriceCents = divRound(boxPriceCents, BigInt(unitsPerBox));
  return Number(unitPriceCents) / 100;
};

// ─────────────────────────────────────────────────────────────
// STORE
// ─────────────────────────────────────────────────────────────

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      currentCustomer: null,
      customer: null,

      setIsOpen: (open) => set({ isOpen: open }),

      setCustomer: (customer) => set({
        currentCustomer: customer,
        customer: customer // Mantener sincronizados
      }),

      setItems: (items) => set({
        items: items.map(i => ({
          ...i,
          cartItemId: i.cartItemId || crypto.randomUUID(),
          final_price: i.final_price || i.price,
          subtotal: i.subtotal || (i.quantity * (i.final_price || i.price)),
          sale_mode: i.sale_mode || 'UNIT', // Default a UNIT
        }))
      }),

      addItem: (product, modifiers = [], options = {}) => {
        const currentItems = get().items;

        // Lógica de mezcla: Si es el mismo producto Y tiene los mismos modificadores, sumamos cantidad.
        // NOTA: cuando la cuenta se divide por comensal se usa forceNewLine, porque
        // cada unidad debe poder asignarse a una persona distinta (2 sándwiches ≠ 1 línea).
        const existingIndex = options.forceNewLine
          ? -1
          : currentItems.findIndex(i =>
              i.id === product.id &&
              JSON.stringify(i.modifiers) === JSON.stringify(modifiers) &&
              i.sale_mode === (product.box_price && product.units_per_box ? 'BOX' : 'UNIT')
            );

        if (existingIndex > -1) {
          const newItems = [...currentItems];
          const item = newItems[existingIndex];
          const newQty = item.quantity + 1;
          newItems[existingIndex] = {
            ...item,
            quantity: newQty,
            subtotal: newQty * item.final_price
          };
          set({ items: newItems });
        } else {
          const basePrice = product.price || 0;
          const hasBoxConversion = Boolean(product.box_price && product.units_per_box);
          const saleMode: SaleMode = hasBoxConversion ? 'BOX' : 'UNIT';
          const unitPrice = calculateUnitPrice(
            basePrice,
            product.box_price,
            product.units_per_box,
            saleMode,
          );

          const cartItemId = crypto.randomUUID();
          const newItem: CartItem = {
            id: product.id || crypto.randomUUID(),
            cartItemId,
            name: product.name,
            price: basePrice,
            final_price: unitPrice,
            quantity: 1,
            subtotal: unitPrice,
            unit: product.unit || 'UNIDAD',
            image_url: product.image_url ?? undefined,
            supplier_id: product.default_supplier_id ?? undefined,
            supplier_name: product.supplier_name || 'Proveedor',
            modifiers: modifiers,
            // `Product.default_destination` es `TicketDestination` (incluye
            // WAREHOUSE); el carrito solo usa 3 destinos de cocina/barra.
            ticket_destination:
              product.default_destination === 'KITCHEN' || product.default_destination === 'BAR'
                ? product.default_destination
                : 'NONE',
            type: product.type || 'FISICO',

            // Conversión de unidad (si el producto la define)
            base_unit: product.unit_base ?? product.unit,
            sale_unit: product.unit_base ?? product.unit,
            conversion_to: product.conversion_to,
            conversion_factor: product.conversion_factor,

            // 🆕 Conversión caja/unidad v110
            box_price: product.box_price,
            units_per_box: product.units_per_box,
            sale_mode: saleMode,

            // 🆕 Multi-sede y tags v110
            // ⚠️ `Product` (db.ts) NO declara `branch_id`; `tags` SÍ existe y es
            // la columna real. `branch_id` se lee con guarda de fila legacy.
            ...(readLegacyBranchId(product) ? { branch_id: readLegacyBranchId(product) } : {}),
            ...(Array.isArray(product.tags) ? { tags: [...product.tags] } : {}),
          };
          set({ items: [...currentItems, newItem] });
        }

        // Abrir el drawer automáticamente si estamos en modo Marketplace
        if (product.default_supplier_id) {
          set({ isOpen: true });
        }
      },

      removeItem: (cartItemId) => set({
        items: get().items.filter(i => i.cartItemId !== cartItemId)
      }),

      // Actualiza campos concretos de un ítem (ej: asignarlo a un comensal o
      // marcarlo como compartido) sin tocar el resto del carrito.
      updateItemFields: (cartItemId, patch) => set({
        items: get().items.map(i => (i.cartItemId === cartItemId ? { ...i, ...patch } : i))
      }),

      updateQuantity: (cartItemId, qty) => set({
        items: get().items.map(i =>
          i.cartItemId === cartItemId
            ? { ...i, quantity: qty, subtotal: qty * i.final_price }
            : i
        )
      }),

      updateItemPrice: (cartItemId, newPrice) => set({
        items: get().items.map(i =>
          i.cartItemId === cartItemId
            ? { ...i, price: newPrice, final_price: newPrice, subtotal: i.quantity * newPrice }
            : i
        )
      }),

      // Cambiar la UNIDAD de venta de un ítem (conversión proporcional).
      // Si vendes en la unidad secundaria, el precio se ajusta y el stock se descuenta equivalente.
      setItemSaleUnit: (cartItemId, saleUnit) => set({
        items: get().items.map(i => {
          if (i.cartItemId !== cartItemId) return i;
          const base = i.base_unit || i.unit;
          const factor = i.conversion_factor || 1;
          let newUnitPrice = i.price;
          if (saleUnit === base) {
            // volver a la unidad base: precio original
            newUnitPrice = i.price;
          } else if (saleUnit === i.conversion_to) {
            // vender en la unidad secundaria: precio = precio_base / factor
            newUnitPrice = i.price / factor;
          }
          return { ...i, sale_unit: saleUnit, final_price: newUnitPrice, subtotal: i.quantity * newUnitPrice };
        })
      }),

      // 🆕 Cambiar el modo de venta: BOX (caja completa) o UNIT (unidad suelta)
      setSaleMode: (cartItemId, mode) => set({
        items: get().items.map(i => {
          if (i.cartItemId !== cartItemId) return i;

          const unitPrice = calculateUnitPrice(
            i.price,
            i.box_price,
            i.units_per_box,
            mode,
          );

          return {
            ...i,
            sale_mode: mode,
            final_price: unitPrice,
            subtotal: i.quantity * unitPrice,
          };
        })
      }),

      increaseQuantity: (cartItemId) => {
        const items = get().items;
        const item = items.find(i => i.cartItemId === cartItemId);
        if (item) {
          get().updateQuantity(cartItemId, item.quantity + 1);
        }
      },

      decreaseQuantity: (cartItemId) => {
        const items = get().items;
        const item = items.find(i => i.cartItemId === cartItemId);
        if (item) {
          if (item.quantity > 1) {
            get().updateQuantity(cartItemId, item.quantity - 1);
          } else {
            get().removeItem(cartItemId);
          }
        }
      },

      clearCart: () => set({ items: [], currentCustomer: null, customer: null }),

      getSummary: () => {
        const items = get().items;
        const subtotal = items.reduce((sum, i) => sum + i.subtotal, 0);
        // En una implementación real, aquí se calcularían impuestos y descuentos globales
        return {
          subtotal,
          total: subtotal,
          taxAmount: 0,
          discount: 0,
          tipAmount: 0
        };
      },

      getTotal: () => get().getSummary().total,

      // ── 🆕 HELPERS FINANCIEROS (Centavos) ──

      getSubtotalInCents: () => {
        const items = get().items;
        const subtotalPesos = items.reduce((sum, i) => sum + i.subtotal, 0);
        return toCentavos(subtotalPesos);
      },

      getTotalInCents: () => {
        const totalPesos = get().getTotal();
        return toCentavos(totalPesos);
      },

      getItemPriceInCents: (cartItemId) => {
        const item = get().items.find(i => i.cartItemId === cartItemId);
        if (!item) return C(0n);
        return toCentavos(item.final_price);
      },

      getItemSubtotalInCents: (cartItemId) => {
        const item = get().items.find(i => i.cartItemId === cartItemId);
        if (!item) return C(0n);
        return toCentavos(item.subtotal);
      },
    }),
    {
      name: 'niiz-cart-storage',
      version: 5, // Incrementamos para limpiar estados incompatibles y añadir sale_mode
      migrate: (persistedState: unknown, version: number) => {
        if (version < 5) {
          return { items: [], isOpen: false, currentCustomer: null, customer: null };
        }
        return persistedState as any;
      },
    }
  )
);

// ─────────────────────────────────────────────────────────────
// HELPERS PÚBLICOS (fuera del store para uso en componentes)
// ─────────────────────────────────────────────────────────────

/**
 * Calcula el precio de un producto según el modo de venta (BOX o UNIT).
 * Útil cuando un componente necesita mostrar el precio antes de añadir al carrito.
 */
export const calculateProductPrice = (
  product: Product,
  saleMode: SaleMode = 'UNIT',
): number => {
  return calculateUnitPrice(
    product.price || 0,
    product.box_price,
    product.units_per_box,
    saleMode,
  );
};

/**
 * Verifica si un producto soporta conversión caja/unidad.
 */
export const supportsBoxConversion = (product: Product): boolean => {
  return Boolean(product.box_price && product.units_per_box && product.units_per_box > 0);
};

/**
 * Calcula cuántas cajas completas y unidades sueltas hay en una cantidad.
 * Ejemplo: 25 unidades con 12 unidades/caja = 2 cajas + 1 unidad
 */
export const breakdownQuantity = (
  quantity: number,
  unitsPerBox: number | undefined,
): { boxes: number; units: number } => {
  if (!unitsPerBox || unitsPerBox === 0) {
    return { boxes: 0, units: quantity };
  }
  const boxes = Math.floor(quantity / unitsPerBox);
  const units = quantity % unitsPerBox;
  return { boxes, units };
};

/**
 * Formatea una cantidad en formato legible (ej: "2 cajas + 3 unidades").
 */
export const formatQuantityBreakdown = (
  quantity: number,
  unitsPerBox: number | undefined,
  unitLabel: string = 'unidades',
  boxLabel: string = 'cajas',
): string => {
  const { boxes, units } = breakdownQuantity(quantity, unitsPerBox);
  if (boxes === 0) return `${units} ${unitLabel}`;
  if (units === 0) return `${boxes} ${boxLabel}`;
  return `${boxes} ${boxLabel} + ${units} ${unitLabel}`;
};